import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };

export default function HomePage() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { axios.get(`${API}/faqs`).then(r => setFaqs(r.data.faqs)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-black text-white">

      {/* ═══ HERO ═══ */}
      <section data-testid="hero-section" className="min-h-screen flex flex-col justify-center px-6 md:px-16 lg:px-28">
        <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: "easeOut" }}>
          <p className="text-zinc-600 text-[10px] tracking-[0.4em] mb-12">Prospera.buzz</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.08] max-w-5xl">
            We don't market businesses.
            <br />
            <span className="text-zinc-500">We shape how they are perceived.</span>
          </h1>
          <p className="text-zinc-400 text-sm md:text-base mt-12 max-w-2xl leading-relaxed tracking-wider">
            Because businesses aren't chosen for what they are. They're chosen for what people believe they are. We help founders and brands build authority, trust, and demand through strategic positioning, storytelling, and growth systems designed to influence perception at scale.
          </p>
          <div className="flex flex-wrap gap-4 mt-14">
            <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20book%20a%20strategy%20call." target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Book a strategy call <ArrowRight className="w-4 h-4" />
            </a>
            <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20discuss%20how%20we%20can%20shape%20the%20perception%20of%20my%20business." target="_blank" rel="noopener noreferrer" data-testid="hero-work-btn"
              className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors inline-flex items-center gap-3">
              Instant chat <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* ═══ THE PERCEPTION PROBLEM ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight leading-[1.15]">
            Most businesses don't have a visibility problem. They have a perception problem.
          </h2>
          <div className="mt-16 space-y-12">
            <div>
              <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">Customers hesitate — not because your product is weak, but because they can't feel why it matters.</p>
            </div>
            <div>
              <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">Investors question — not because the numbers are wrong, but because the story doesn't land.</p>
            </div>
            <div>
              <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">Talent overlooks you — not because the role is bad, but because your brand doesn't signal ambition.</p>
            </div>
            <div>
              <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">Markets ignore you — not because you lack value, but because the market doesn't clearly understand your value.</p>
            </div>
          </div>
          <p className="text-zinc-500 text-sm mt-16 tracking-wider leading-relaxed">
            The gap between what a business is and what people believe it is — that's where growth is lost. That's what we close.
          </p>
          <a href="#framework" className="inline-flex items-center gap-3 text-xs text-zinc-500 hover:text-white tracking-[0.2em] transition-colors mt-10">
            See how we close it <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </motion.section>

      {/* ═══ THE COST OF BEING MISUNDERSTOOD ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          The cost of being misunderstood is not measured in impressions or clicks. It's measured in the deals that never happened, the talent that never applied, and the customers who chose someone who simply explained their value better.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-20 bg-white/10 max-w-4xl">
          {[
            { t: "You're competing on price because your positioning doesn't justify premium", d: "When the market can't see the difference between you and the alternative, they default to the cheaper option. That's not a sales problem. That's a perception problem." },
            { t: "Your best customers found you by accident, not by design", d: "If your growth depends on luck and referrals, you've built a business that works despite its brand — not because of it. Engineered perception creates engineered demand." },
            { t: "You've outgrown your brand but the market still sees the old version", d: "You've evolved. Your capabilities have expanded. But your digital presence, your narrative, your visual identity — they tell the old story. The market is making decisions based on an outdated perception." },
            { t: "AI search doesn't know you exist", d: "When someone asks ChatGPT, Perplexity, or Gemini for a recommendation in your category — your name doesn't come up. Not because you're not qualified. Because AI has no reason to trust you yet." }
          ].map(s => (
            <div key={s.t} className="bg-black p-10">
              <p className="text-sm tracking-wider leading-relaxed">{s.t}</p>
              <p className="text-zinc-500 text-xs mt-6 leading-relaxed tracking-wider">{s.d}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ═══ HOW PERCEPTION SHAPES DEMAND ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          The brands you admire didn't get chosen because they were objectively the best. They got chosen because they shaped what "best" means in their category.
        </h2>
        <div className="mt-20 max-w-3xl space-y-16">
          <div>
            <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">
              Apple doesn't sell the most powerful computers. They sell the belief that creative people deserve tools as refined as their taste. The product follows the perception.
            </p>
          </div>
          <div>
            <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">
              Nike doesn't sell the fastest shoes. They sell the belief that anyone who moves their body is an athlete. The product follows the perception.
            </p>
          </div>
          <div>
            <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">
              McKinsey doesn't sell the smartest consultants. They sell the belief that hiring McKinsey means you're serious about the problem. The expertise follows the perception.
            </p>
          </div>
          <div className="pt-8 border-t border-white/5">
            <p className="text-zinc-400 text-sm tracking-wider leading-relaxed">
              Perception is not manipulation. It's clarity. It's making what's true about your business visible, felt, and impossible to ignore. Every business has value. Most just haven't learned how to make the market feel it.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ═══ OUR FRAMEWORK ═══ */}
      <motion.section id="framework" variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          We don't start with marketing. We start with the question every business should answer before spending a single rupee on growth.
        </h2>
        <p className="text-zinc-500 text-sm mt-8 tracking-wider max-w-2xl leading-relaxed">
          "What should the market believe about us — and what's preventing that belief right now?"
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px mt-20 bg-white/10 max-w-5xl">
          {[
            { n: "01", t: "Positioning", d: "What is the one idea your business should own in the market's mind? Not your tagline. Not your mission statement. The irreducible belief that makes you the obvious choice." },
            { n: "02", t: "Storytelling", d: "Facts inform. Stories transform. We build the narrative architecture that turns your value proposition into something people remember, repeat, and act on." },
            { n: "03", t: "Authority building", d: "Trust isn't claimed. It's demonstrated. Through founder presence, expert content, AI visibility, media recognition, and a digital footprint that signals credibility at every touchpoint." },
            { n: "04", t: "Growth systems", d: "Perception without distribution is philosophy. We build the systems — SEO, ads, content engines, AI visibility — that put the right perception in front of the right people at the right time." }
          ].map(s => (
            <div key={s.n} className="bg-black p-10">
              <p className="text-zinc-700 text-2xl">{s.n}</p>
              <p className="text-sm tracking-wider mt-6">{s.t}</p>
              <p className="text-zinc-500 text-xs mt-4 leading-relaxed tracking-wider">{s.d}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ═══ WHAT WE ACTUALLY DO ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl leading-[1.15]">Not marketing services. Perception infrastructure.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-20 bg-white/10 max-w-5xl">
          {[
            { cat: "Positioning", items: [
              { t: "Brand Strategy", to: "/services/brand-strategy" },
              { t: "Founder Branding", to: "/services/founder-branding" },
              { t: "Personal Branding", to: "/services/personal-branding" }
            ]},
            { cat: "Storytelling", items: [
              { t: "Brand Films & Documentaries", to: "/services/brand-storytelling" },
              { t: "Content Marketing", to: "/services/content-marketing" },
              { t: "Ad Studio — Performance Creatives", to: "/ad-studio" }
            ]},
            { cat: "Authority building", items: [
              { t: "AI Search Optimization", to: "/services/ai-search-optimization" },
              { t: "Generative Engine Optimization", to: "/services/geo" },
              { t: "SEO", to: "/services/seo" },
              { t: "LinkedIn Growth", to: "/services/linkedin-growth" }
            ]},
            { cat: "Growth systems", items: [
              { t: "Google Ads", to: "/services/google-ads" },
              { t: "Meta Ads", to: "/services/meta-ads" },
              { t: "Social Media Marketing", to: "/services/social-media-marketing" },
              { t: "Remote CMO", to: "/services/remote-cmo" }
            ]}
          ].map(c => (
            <div key={c.cat} className="bg-black p-10">
              <p className="text-xs tracking-[0.2em] text-zinc-500 mb-6">{c.cat}</p>
              <div className="space-y-4">
                {c.items.map(i => (
                  <Link key={i.t} to={i.to} className="block text-sm tracking-wider text-zinc-300 hover:text-white transition-colors group">
                    {i.t} <span className="text-zinc-700 group-hover:text-zinc-400 transition-colors ml-2">→</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ═══ WHO WE HELP ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          We work with people who have already built something valuable — and need the world to see it that way.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-20 bg-white/10 max-w-5xl">
          {[
            "Founders & CEOs", "Professional services", "Hospitality & luxury", "Real estate",
            "D2C & e-commerce", "Healthcare", "Manufacturing", "High-growth startups"
          ].map(w => (
            <div key={w} className="bg-black p-8">
              <p className="text-xs tracking-wider text-zinc-400">{w}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ═══ SELECTED WORK (PLACEHOLDER) ═══ */}
      {/* NOTE TO SELF: Keep asking Ashish for case study content. This section needs real work examples. */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl leading-[1.15]">
          We'll let the work speak. Soon.
        </h2>
        <p className="text-zinc-500 text-xs mt-6 tracking-wider max-w-lg leading-relaxed">
          We're curating our most transformative projects for this section. In the meantime, if you'd like to see what we've done for businesses in your industry — ask us directly.
        </p>
        <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20see%20examples%20of%20your%20work%20in%20my%20industry." target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors mt-10">
          Request portfolio <ArrowRight className="w-4 h-4" />
        </a>
      </motion.section>

      {/* ═══ RESULTS & TRANSFORMATIONS ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          What happens when perception finally matches value.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-20 bg-white/10 max-w-5xl">
          {[
            { before: "A product company competing on price", after: "A brand commanding premium because the market understands why they're different" },
            { before: "A founder invisible in their own industry", after: "A recognized voice whose LinkedIn generates more leads than their ads" },
            { before: "A business that AI search didn't know existed", after: "A business that ChatGPT and Perplexity recommend by name" },
            { before: "An artist with talent and zero visibility", after: "An artist with millions of views, brand deals, and editorial playlist placements" },
            { before: "A service company lost in a crowded market", after: "A company with a waitlist — because the story they tell makes the decision obvious" },
            { before: "A team creating content nobody engages with", after: "A team whose content gets shared because it says what the audience already feels but couldn't articulate" }
          ].map((t, i) => (
            <div key={i} className="bg-black p-10">
              <p className="text-zinc-600 text-[10px] tracking-[0.2em] mb-3">Before</p>
              <p className="text-zinc-500 text-xs tracking-wider leading-relaxed">{t.before}</p>
              <p className="text-zinc-600 text-[10px] tracking-[0.2em] mt-6 mb-3">After</p>
              <p className="text-zinc-300 text-xs tracking-wider leading-relaxed">{t.after}</p>
            </div>
          ))}
        </div>
        <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20understand%20what%20a%20perception%20strategy%20could%20look%20like%20for%20my%20business." target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-3 text-xs text-zinc-500 hover:text-white tracking-[0.2em] transition-colors mt-16">
          What could this look like for my business? <ArrowRight className="w-4 h-4" />
        </a>
      </motion.section>

      {/* ═══ INSIGHTS & THINKING ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl leading-[1.15]">
          Ideas we think about. And think you should too.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-20 bg-white/10 max-w-4xl">
          {[
            { t: "The businesses winning in AI search aren't doing more marketing. They're doing more meaning.", to: "/academy" },
            { t: "Your founder's LinkedIn is a bigger growth lever than your entire ad budget. Most founders ignore it.", to: "/services/founder-branding" },
            { t: "SEO gets you found. GEO gets you recommended. The difference will define the next decade.", to: "/services/geo" },
            { t: "The best ad in the world fails if it tells the wrong story. The worst production succeeds if the story is right.", to: "/ad-studio" }
          ].map(s => (
            <Link key={s.t} to={s.to} className="bg-black p-10 hover:bg-white/[0.02] transition-colors group block">
              <p className="text-sm tracking-wider leading-relaxed text-zinc-300 transition-colors">{s.t}</p>
              <p className="text-zinc-700 text-[10px] tracking-[0.2em] mt-6 group-hover:text-zinc-400 transition-colors">Read more →</p>
            </Link>
          ))}
        </div>
      </motion.section>

      {/* ═══ WHY BUSINESSES CHOOSE PROSPERA ═══ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          We're not the agency that executes your marketing plan. We're the partner that challenges whether your plan is even solving the right problem.
        </h2>
        <div className="mt-20 max-w-3xl space-y-12">
          {[
            "We think in systems, not campaigns. A campaign ends. A system compounds.",
            "We start with positioning, not pixels. If the message is wrong, the medium doesn't matter.",
            "We build for AI-first discovery. The future of being found is being recommended. We engineer that.",
            "We understand that the founder IS the brand. Especially before product-market fit and especially after.",
            "We operate globally from Chandigarh, Dubai, and Toronto. Three time zones. One obsession.",
            "We work with a limited number of clients at a time. Not because we can't scale. Because perception engineering requires depth, not volume."
          ].map((s, i) => (
            <div key={i} className="flex gap-6">
              <p className="text-zinc-700 text-xs tracking-wider flex-shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</p>
              <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ═══ FAQ ═══ */}
      {faqs.length > 0 && (
        <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
          data-testid="faq-section" className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 max-w-5xl">
            <div className="lg:col-span-4">
              <p className="text-zinc-600 text-[10px] tracking-[0.4em] mb-8">Questions</p>
              <h2 className="text-2xl tracking-tight">Things worth asking.</h2>
            </div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible>
                {faqs.map(f => (
                  <AccordionItem key={f.id} value={f.id} className="border-white/10" data-testid={`faq-item-${f.id}`}>
                    <AccordionTrigger className="text-left text-white hover:no-underline text-xs tracking-wider py-6">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-zinc-400 text-[10px] leading-relaxed tracking-wider pb-6">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </motion.section>
      )}

      {/* ═══ FINAL CONVERSION ═══ */}
      <section className="py-32 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl lg:text-4xl tracking-tight leading-[1.15]">
            The question isn't whether your business is good enough. It's whether the market can tell.
          </h2>
          <p className="text-zinc-500 text-sm mt-8 tracking-wider leading-relaxed">
            If the answer is "not yet" — that's not a failure. It's an opportunity. And the best time to engineer the right perception is before your competitors do.
          </p>
          <div className="flex flex-wrap gap-4 mt-14">
            <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20discuss%20how%20we%20can%20shape%20the%20perception%20of%20my%20business." target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Book a strategy call <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/academy" data-testid="cta-academy"
              className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors">
              Explore our thinking
            </Link>
          </div>
          <div className="flex flex-wrap gap-6 mt-10">
            <a href="tel:+916239547599" className="text-zinc-600 hover:text-white text-xs tracking-wider transition-colors inline-flex items-center gap-2">
              <Phone className="w-3 h-3" /> +91 6239 547 599
            </a>
            <span className="text-zinc-700 text-xs tracking-wider">Chandigarh · Dubai · Toronto</span>
          </div>
        </motion.div>
      </section>

      {/* ═══ SEO ═══ */}
      <section className="py-12 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="flex flex-wrap gap-2">
          {[
            "perception engineering agency", "authority building agency", "brand positioning consultant",
            "AI visibility agency", "generative engine optimization", "founder branding agency",
            "brand storytelling agency", "strategic positioning", "business authority building",
            "perception shaping agency", "brand authority consultant", "hire brand strategist",
            "AI search optimization", "GEO agency", "business growth partner",
            "brand perception consultant India", "brand authority agency Dubai",
            "strategic brand partner Toronto", "McKinsey of marketing", "premium brand consultancy"
          ].map(kw => (
            <span key={kw} className="text-[10px] text-zinc-800 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
